import type { TFunction } from 'i18next'
import type { ActivityLog } from '@/types'
import { formatCurrency } from './format'

const DOCUMENT_ACTIVITY_EVENTS = new Set([
  'document.uploaded',
  'document.linked',
  'document.copied',
])

function property(a: ActivityLog, key: string): string | null {
  const value = a.properties?.[key]
  return typeof value === 'string' && value !== '' ? value : null
}

function numberProperty(a: ActivityLog, key: string): number | null {
  const value = a.properties?.[key]
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() && Number.isFinite(+value)) {
    return +value
  }
  return null
}

function defaultActivityLabel(a: ActivityLog, t: TFunction): string {
  return a.event_type
    ? t(`activity.events.${a.event_type.replace(/\./g, '_')}`, {
        defaultValue: a.event_type.replace(/[._]/g, ' '),
      })
    : '-'
}

/**
 * Human-readable label for the client Recent Activity Preview.
 *
 * New document events carry enough metadata to distinguish a new upload from
 * an existing/copy attachment and to name the entity that received it. Legacy
 * rows fall back to the original event label.
 */
export function activityLabel(a: ActivityLog, t: TFunction): string {
  const fallback = defaultActivityLabel(a, t)
  if (
    a.event_type === 'payment.pending' ||
    a.event_type === 'payment.received'
  ) {
    const amount = numberProperty(a, 'amount')
    if (amount === null) return fallback

    const state = a.event_type === 'payment.pending' ? 'pending' : 'received'
    return t(`activity.events.payment_${state}_with_amount`, {
      amount: formatCurrency(amount, 'EUR', 2),
      defaultValue: fallback,
    })
  }
  if (!DOCUMENT_ACTIVITY_EVENTS.has(a.event_type)) return fallback

  const ownerType = property(a, 'document_owner_type')
  if (!ownerType) return fallback

  const action =
    property(a, 'document_action') ??
    (a.event_type === 'document.copied'
      ? 'copied'
      : a.event_type === 'document.linked'
        ? 'attached'
        : 'uploaded')
  const kind = property(a, 'document_kind') ?? 'document'
  const labelKey =
    action === 'uploaded' && kind === 'report_photo'
      ? 'report_photo_uploaded'
      : action === 'uploaded' && kind === 'signature'
        ? 'signature_uploaded'
        : action

  if (!['uploaded', 'attached', 'copied'].includes(action)) return fallback

  const entity = t(`activity.document.entities.${ownerType}`, {
    defaultValue: ownerType.replace(/_/g, ' '),
  })

  return t(`activity.document.${labelKey}`, {
    entity,
    defaultValue: fallback,
  })
}
